import React from 'react';
import PropTypes from 'prop-types';
import { faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { WizardFieldGroup, WizardSelect } from './WizardField';
import {
  USE_CASE_OPTIONS,
  TIMELINE_OPTIONS,
  USED_SOFTWARE_OPTIONS,
  VOLUME_OPTIONS,
} from '../utils/constants';
import { useTranslation } from 'react-i18next';

/**
 * Step 2: Use Case & Timeline — all fields optional, modern Radix selects.
 */
const WizardStep2 = ({ formData, updateField, onNext, onBack, onSkip }) => {
  const { t } = useTranslation('onboarding');
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-800 m-0">
          {t('wizard.useCaseTimelineTitle', 'Use case & timeline')}
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          {t(
            'wizard.useCaseTimelineHint',
            'Helps us point you at the right tools first — every field is optional.',
          )}
        </p>
      </div>

      <WizardFieldGroup
        id="primaryUseCase"
        label={t('wizard.primaryUseCase', "What's your primary use case?")}
      >
        <WizardSelect
          id="primaryUseCase"
          value={formData.primaryUseCase}
          onChange={(v) => updateField('primaryUseCase', v)}
          options={USE_CASE_OPTIONS}
          placeholder={t('wizard.selectUseCase', 'Select use case')}
        />
      </WizardFieldGroup>

      <WizardFieldGroup
        id="complianceTimeline"
        label={t('wizard.complianceTimeline', 'When do you need to be CBAM compliant?')}
      >
        <WizardSelect
          id="complianceTimeline"
          value={formData.complianceTimeline}
          onChange={(v) => updateField('complianceTimeline', v)}
          options={TIMELINE_OPTIONS}
          placeholder={t('wizard.selectTimeline', 'Select timeline')}
        />
      </WizardFieldGroup>

      <WizardFieldGroup
        id="hasUsedCbamSoftwareBefore"
        label={t('wizard.usedSoftwareBefore', 'Have you used any CBAM software before?')}
      >
        <WizardSelect
          id="hasUsedCbamSoftwareBefore"
          value={formData.hasUsedCbamSoftwareBefore}
          onChange={(v) => updateField('hasUsedCbamSoftwareBefore', v)}
          options={USED_SOFTWARE_OPTIONS}
          placeholder={t('wizard.select', 'Select')}
        />
      </WizardFieldGroup>

      <WizardFieldGroup
        id="annualVolume"
        label={t('wizard.annualVolume', "What's your annual import / production volume?")}
      >
        <WizardSelect
          id="annualVolume"
          value={formData.annualVolume}
          onChange={(v) => updateField('annualVolume', v)}
          options={VOLUME_OPTIONS}
          placeholder={t('wizard.selectVolume', 'Select volume')}
        />
      </WizardFieldGroup>

      <div className="flex justify-between items-center mt-8 pt-4 border-t border-slate-100">
        <CgRadixButton
          variant="ghost"
          size="lg"
          onClick={onBack}
          icon={faArrowLeft}
        >
          {t('common:actions.back', 'Back')}
        </CgRadixButton>
        <div className="flex gap-2">
          <CgRadixButton
            variant="ghost"
            size="lg"
            onClick={onSkip}
          >
            {t('actions.skip', 'Skip')}
          </CgRadixButton>
          <CgRadixButton
            variant="primary"
            size="lg"
            onClick={onNext}
            icon={faArrowRight}
          >
            {t('common:actions.continue', 'Continue')}
          </CgRadixButton>
        </div>
      </div>
    </div>
  );
};

WizardStep2.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default WizardStep2;
